// src/components/Common/CookieConsent.tsx
import { useState, useEffect, useCallback } from 'react';
import { CONSENT_CHANGED_EVENT, getConsent, setConsent } from '../../utils/consent';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  const showIfUndecided = useCallback(() => {
    setVisible(getConsent() === null);
  }, []);

  useEffect(() => {
    // No artificial delay before showing it.
    //
    // There used to be a one-second wait "so it doesn't flash on initial load",
    // which meant the visitor was tracked for a second before being asked. That
    // is exactly backwards: nothing non-essential loads until a decision now, so
    // the sooner the question is asked the sooner analytics may legitimately run.
    showIfUndecided();

    // Reappears if consent is withdrawn from the cookie policy page.
    window.addEventListener(CONSENT_CHANGED_EVENT, showIfUndecided);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, showIfUndecided);
  }, [showIfUndecided]);

  // Both paths go through setConsent, which records the decision AND acts on it.
  // Previously each wrote its own localStorage key and only the decline branch
  // did anything - and even that only to ads, in the current page, once.
  const accept = () => {
    setConsent('accepted');
    setVisible(false);
  };

  const decline = () => {
    setConsent('declined');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 shadow-lg">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-sm text-gray-300 text-center sm:text-left">
          We use cookies and similar technologies to analyse traffic and improve your experience. Nothing but what the site needs to work is loaded until you choose.{' '}
          <a href="/privacy" className="text-orange-400 hover:text-orange-300 underline">
            Privacy Policy
          </a>{' '}
          &middot;{' '}
          <a href="/cookies" className="text-orange-400 hover:text-orange-300 underline">
            Cookie Policy
          </a>
        </p>
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={decline}
            className="px-4 py-2 text-sm text-gray-300 hover:text-white border border-gray-600 rounded-lg hover:border-gray-500 transition-colors"
          >
            Decline
          </button>
          <button
            onClick={accept}
            className="px-4 py-2 text-sm text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
